import { DatePicker, Input, Select, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import {
  fetchStockPurchaseOrderDetail,
  fetchStockPurchaseOrderOptions,
  fetchStockPurchaseOrders,
} from "../../apis/inventory.api";
import {
  IStockPurchaseOrder,
  IStockPurchaseOrderItem,
  IStockPurchaseOrderOptions,
  IStockPurchaseOrderParams,
} from "../../interfaces/inventory.interface";
import { DeliveryDate, RECEIVE_STATUS_COLOR, ZOHO_PO_URL, fmtDate, qty } from "./incoming_stock_table";

const PAGE_SIZE = 20;

// PO columns the server can sort on, keyed by the column key.
const SORTABLE: Record<string, string> = {
  date: "date",
  total: "total",
  purchase_order_number: "purchase_order_number",
  vendor_name: "vendor_name",
};

const BILLED_STATUS_COLOR: Record<string, string> = {
  to_be_billed: "default",
  partially_billed: "orange",
  billed: "green",
};

const rupees = (value: string | null | undefined) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const itemColumns: ColumnsType<IStockPurchaseOrderItem> = [
  {
    title: "Item",
    dataIndex: "name",
    key: "name",
    width: 360,
    render: (name: string, item) => (
      <Space direction="vertical" size={0}>
        <span>{name}</span>
        {item.variant_handle && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.variant_handle}
          </Typography.Text>
        )}
      </Space>
    ),
  },
  {
    title: "Destination",
    dataIndex: "location_name",
    key: "location_name",
    width: 200,
    render: (v: string) => v || "-",
  },
  {
    title: "Outstanding",
    dataIndex: "quantity_pending",
    key: "quantity_pending",
    width: 120,
    // quantity_pending excludes in-transit; outstanding here is what the vendor still owes us.
    render: (_: unknown, item) => {
      const owed = Number(item.quantity) - Number(item.quantity_received) - Number(item.quantity_cancelled);
      return owed > 0 ? (
        <strong>
          {qty(String(owed))} {item.unit}
        </strong>
      ) : (
        <Tag color="green">received</Tag>
      );
    },
  },
  {
    title: "In Transit",
    dataIndex: "quantity_intransit",
    key: "quantity_intransit",
    width: 100,
    render: (v: string) => (Number(v) > 0 ? <Tag color="blue">{qty(v)}</Tag> : "-"),
  },
  { title: "Ordered", dataIndex: "quantity", key: "quantity", width: 90, render: qty },
  { title: "Received", dataIndex: "quantity_received", key: "quantity_received", width: 90, render: qty },
  { title: "Billed", dataIndex: "quantity_billed", key: "quantity_billed", width: 90, render: qty },
  { title: "Rate", dataIndex: "rate", key: "rate", width: 110, render: rupees },
];

// Items load on first expand and are kept, so re-opening a row does not refetch.
// Cache value: undefined = not loaded yet, null = the request failed, [] = PO has no items.
type ItemsCache = Record<number, IStockPurchaseOrderItem[] | null | undefined>;

function PurchaseOrderItems({
  po,
  cache,
  onLoaded,
}: {
  po: IStockPurchaseOrder;
  cache: ItemsCache;
  onLoaded: (id: number, items: IStockPurchaseOrderItem[] | null | undefined) => void;
}) {
  const items = cache[po.id];
  useEffect(() => {
    if (items !== undefined) return;
    fetchStockPurchaseOrderDetail(po.id).then((detail) => onLoaded(po.id, detail ? detail.items : null));
  }, [po.id, items, onLoaded]);

  if (items === undefined) return <Spin size="small" />;
  if (items === null) {
    return (
      <Typography.Text type="danger">
        Couldn't load this PO's items.{" "}
        <Typography.Link onClick={() => onLoaded(po.id, undefined)}>Retry</Typography.Link>
      </Typography.Text>
    );
  }
  if (items.length === 0) {
    return (
      <Typography.Text type="secondary">
        This PO's items haven't been pulled from Zoho yet. The sync catches up every 15 minutes.
      </Typography.Text>
    );
  }
  return (
    <Table
      dataSource={items}
      columns={itemColumns}
      rowKey="id"
      size="small"
      pagination={false}
      scroll={{ x: 1160 }}
    />
  );
}

export default function StockPurchaseOrdersTable() {
  const [rows, setRows] = useState<IStockPurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [options, setOptions] = useState<IStockPurchaseOrderOptions | null>(null);
  const [itemsCache, setItemsCache] = useState<ItemsCache>({});
  const [searchText, setSearchText] = useState("");
  const [params, setParams] = useState<IStockPurchaseOrderParams>({
    page: 1,
    page_size: PAGE_SIZE,
    ordering: "-date",
  });

  useEffect(() => {
    fetchStockPurchaseOrderOptions().then(setOptions);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStockPurchaseOrders(params).then((response) => {
      if (cancelled) return;
      if (response) {
        setRows(response.data);
        setTotal(response.meta.pagination.count);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [params]);

  const onItemsLoaded = useCallback(
    (id: number, items: IStockPurchaseOrderItem[] | null | undefined) =>
      setItemsCache((prev) => ({ ...prev, [id]: items })),
    []
  );

  // Any filter change goes back to page 1.
  const update = (patch: Partial<IStockPurchaseOrderParams>) =>
    setParams((prev) => ({ ...prev, page: 1, ...patch }));

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<IStockPurchaseOrder> | SorterResult<IStockPurchaseOrder>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = s?.order ? SORTABLE[String(s.columnKey)] : undefined;
    const ordering = field ? `${s.order === "descend" ? "-" : ""}${field}` : "-date";
    setParams((prev) => ({
      ...prev,
      page: ordering === prev.ordering ? pagination.current || 1 : 1,
      ordering,
    }));
  };

  const sortOrderFor = (field: string) => {
    if (params.ordering === field) return "ascend" as const;
    if (params.ordering === `-${field}`) return "descend" as const;
    return null;
  };

  const statusOptions = (list?: { value: string; label: string; count: number }[]) =>
    (list || []).map((o) => ({ value: o.value, label: `${o.label} (${o.count})` }));

  const columns: ColumnsType<IStockPurchaseOrder> = [
    {
      title: "PO",
      dataIndex: "purchase_order_number",
      key: "purchase_order_number",
      width: 130,
      fixed: "left",
      sorter: true,
      sortOrder: sortOrderFor("purchase_order_number"),
      render: (value: string, po) => (
        <a href={`${ZOHO_PO_URL}/${po.zoho_purchase_order_id}`} target="_blank" rel="noreferrer">
          {value}
        </a>
      ),
    },
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      width: 240,
      fixed: "left",
      sorter: true,
      sortOrder: sortOrderFor("vendor_name"),
    },
    {
      title: "PO Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor("date"),
      render: fmtDate,
    },
    {
      title: "Delivery Date",
      key: "delivery_date",
      width: 160,
      render: (_: unknown, po) => (
        <DeliveryDate date={po.delivery_date || po.expected_delivery_date} poDate={po.date} />
      ),
    },
    {
      title: "Receive Status",
      key: "received_status",
      width: 170,
      render: (_: unknown, po) => (
        <Tag color={RECEIVE_STATUS_COLOR[po.received_status] || "default"}>
          {po.received_status_formatted || "-"}
        </Tag>
      ),
    },
    {
      title: "Bill Status",
      key: "billed_status",
      width: 150,
      render: (_: unknown, po) => (
        <Tag color={BILLED_STATUS_COLOR[po.billed_status] || "default"}>{po.billed_status_formatted || "-"}</Tag>
      ),
    },
    {
      title: "Destination",
      key: "destinations",
      width: 230,
      render: (_: unknown, po) => po.destinations || <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: "Items",
      key: "item_count",
      width: 90,
      render: (_: unknown, po) =>
        po.item_count > 0 ? (
          po.item_count
        ) : (
          <Tooltip title="Items not pulled from Zoho yet">
            <Tag>pending</Tag>
          </Tooltip>
        ),
    },
    {
      title: "Outstanding Qty",
      key: "items_outstanding",
      width: 140,
      render: (_: unknown, po) =>
        po.items_outstanding === null ? (
          <Tooltip title="Zoho's yet-to-receive figure; line items not pulled yet">
            <Typography.Text type="secondary">{qty(po.quantity_yet_to_receive)}</Typography.Text>
          </Tooltip>
        ) : (
          <strong>{qty(po.items_outstanding)}</strong>
        ),
    },
    {
      title: "Ordered Qty",
      dataIndex: "total_ordered_quantity",
      key: "total_ordered_quantity",
      width: 110,
      render: qty,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 140,
      sorter: true,
      sortOrder: sortOrderFor("total"),
      render: rupees,
    },
    {
      title: "Branch",
      dataIndex: "location_name",
      key: "location_name",
      width: 170,
    },
  ];

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 pb-3">
        <Input.Search
          value={searchText}
          onChange={(ev) => setSearchText(ev.target.value)}
          onSearch={(value) => update({ search: value })}
          style={{ width: 280 }}
          placeholder="Search PO number or vendor..."
          allowClear
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="Receive status"
          value={params.received_status || []}
          onChange={(value: string[]) => update({ received_status: value })}
          options={statusOptions(options?.received_status)}
          maxTagCount="responsive"
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 180 }}
          placeholder="Bill status"
          value={params.billed_status || []}
          onChange={(value: string[]) => update({ billed_status: value })}
          options={statusOptions(options?.billed_status)}
          maxTagCount="responsive"
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          placeholder="All vendors"
          value={params.zoho_vendor_id}
          onChange={(value?: string) => update({ zoho_vendor_id: value })}
          options={(options?.vendors || []).map((v) => ({
            value: v.zoho_vendor_id,
            label: `${v.vendor_name} (${v.count})`,
          }))}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="All branches"
          value={params.branch_location_id || []}
          onChange={(value: string[]) => update({ branch_location_id: value })}
          options={(options?.branches || []).map((b) => ({
            value: b.zoho_location_id,
            label: `${b.location_name} (${b.count})`,
          }))}
          maxTagCount="responsive"
        />
        <DatePicker.RangePicker
          placeholder={["PO date from", "to"]}
          onChange={(range) => {
            const [from, to] = (range || []) as [Dayjs | null, Dayjs | null];
            update({
              date_from: from ? from.format("YYYY-MM-DD") : undefined,
              date_to: to ? to.format("YYYY-MM-DD") : undefined,
            });
          }}
        />
      </div>

      <Table
        dataSource={rows}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1850 }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (po) => (
            <PurchaseOrderItems po={po} cache={itemsCache} onLoaded={onItemsLoaded} />
          ),
        }}
        pagination={{
          current: params.page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count} purchase orders`,
        }}
      />
    </div>
  );
}
